﻿import { Coordinate } from 'ol/coordinate';
import { getCenter } from 'ol/extent';

import Geometry from 'ol/geom/Geometry';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import MultiLineString from 'ol/geom/MultiLineString';

import { BaseGeometryExtension, GeometryExtensionUtilities } from './BaseGeometryExtension';
import { DEGREES_360 } from '../../utilities/DateLineUtilities';

export class MultiLineStringExtension extends BaseGeometryExtension<MultiLineString> {

    public getCoordinates() {
        return this.geometry.getCoordinates();
    }

    public setCoordinates(coordinates: Coordinate[][]): void {
        this.geometry.setCoordinates(coordinates);
    }

    public intersectsExtent(extent: [number, number, number, number]) {
        return this.geometry.intersectsExtent(extent);
    }

    public getSingleGeometry() {
        return new LineString(this.geometry.getCoordinates()[0]);
    }

    public insertGeometry(geometry: Geometry, path: number[]): number[] | undefined {
        const coords = this.getCoordinates() ?? [];

        if (geometry instanceof LineString) {
            const position = GeometryExtensionUtilities.getPositionFromPath1(path);
            this.setCoordinates(GeometryExtensionUtilities.modifyLineInMultiLine(position, coords, geometry.getCoordinates()));
            return position;
        }
        else if (geometry instanceof Point) {
            const position = GeometryExtensionUtilities.getPositionFromPath2(path);
            this.setCoordinates(GeometryExtensionUtilities.modifyPointInMultiLine(position, coords, geometry.getCoordinates()));
            return position;
        }
        return undefined
    }

    public replaceGeometry(geometry: Geometry, path: number[]): number[] | undefined {
        const coords = this.getCoordinates() ?? [];

        if (geometry instanceof MultiLineString) {
            this.setCoordinates(geometry.getCoordinates().slice())
            return [];
        }
        else if (geometry instanceof LineString) {
            const position = GeometryExtensionUtilities.getPositionFromPath1(path);
            this.setCoordinates(GeometryExtensionUtilities.modifyLineInMultiLine(position, coords, geometry.getCoordinates(), true));
            return position;
        }
        else if (geometry instanceof Point) {
            const position = GeometryExtensionUtilities.getPositionFromPath2(path);
            this.setCoordinates(GeometryExtensionUtilities.modifyPointInMultiLine(position, coords, geometry.getCoordinates(), true));
            return position;
        }

        return undefined
    }

    public removeGeometry(path: number[]): number[] | undefined {
        const coords = this.getCoordinates() ?? [];

        if (path.length >= 2 && coords[path[0]][path[1]]) {
            const position = GeometryExtensionUtilities.getPositionFromPath2(path);
            this.setCoordinates(GeometryExtensionUtilities.modifyPointInMultiLine(position, coords));
            return position;
        }
        else if (path.length >= 1 && coords[path[0]]) {
            const position = GeometryExtensionUtilities.getPositionFromPath1(path);
            this.setCoordinates(GeometryExtensionUtilities.modifyLineInMultiLine(position, coords));
            return position;
        }

        return undefined;
    }

    public canRemove(path: number[]): boolean {
        const coords = this.getCoordinates() ?? [];

        if (path.length >= 1) {
            const line = coords[path[0]];
            if (line) {
                // remove line
                if (path.length == 1)
                    return coords.length > 1;
                // remove point
                else
                    return line.length > 2 && !!line[path[1]];
            }
        }

        return false;
    }

    public groupSectionsAtDateLine() {
        const coords = this.geometry.getLineStrings().map(line => {
            const coordinate = getCenter(line.getExtent());
            if (GeometryExtensionUtilities.shouldWrapLongitude(coordinate[0]))
                line.translate(DEGREES_360, 0);
            return line.getCoordinates();
        })
        this.setCoordinates(coords);
    }

    public mergeSnappedPoints() {
        var mergedSnapped = false;
        const result: Coordinate[][] = [];
        const lines = this.getCoordinates();

        for (var line of lines) {
            if (line.length > 2) {
                let merged = GeometryExtensionUtilities.mergeSnappedPoints(line);
                if (merged) {
                    mergedSnapped = true;
                    line = merged;
                }
            }

            result.push(line);
        }

        if (mergedSnapped)
            this.setCoordinates(result);

        return mergedSnapped;
    }
}